/**
 * Version Control Service
 *
 * Handles version checking and update management for web application.
 * Version info comes from the notification API's `versionInfo`. checkNow() is
 * called by the layout when a page opens (the layout remounts on every route
 * change) and when the tab becomes visible again, and asks at most once per
 * RECHECK_MS.
 *
 * Why here (2026-10-05): the only caller used to be NotificationDropdown, which
 * the header never renders (it always passes handleToggleNotification, so the
 * button branch wins). The update popup was mounted and never fed — no tenant
 * on any cluster could see it. And the dropdown polled every 30 s, so feeding
 * it there would have popped the modal up in the middle of somebody typing.
 *
 * Why not just once per page load (the first version of this fix): a tab
 * opened before a deploy never asked again, and reloading it fetches the new
 * bundle, which already matches the announcement — so the popup could never
 * appear for anybody. Navigating and coming back to the tab are both moments
 * nobody is mid-form, so they are when it asks.
 */

import apiClient from './apiClient';

export interface VersionCheckResponse {
  updateAvailable: boolean;
  forceUpdate: boolean;
  updateType: 'optional' | 'recommended' | 'force';
  latestVersion: string;
  clientVersion: string;
  storeUrl?: string;
  updateTitle?: string;
  updateMessage?: string;
  releaseNotes?: string;
  releaseDate?: string;
  isMaintenanceMode: boolean;
  maintenanceMessage?: string;
  featureFlags?: Record<string, boolean>;
}

export interface VersionConfig {
  id: string;
  platform: 'web' | 'android' | 'ios';
  currentVersion: string;
  minimumVersion: string;
  updateType: 'optional' | 'recommended' | 'force';
  buildNumber?: number;
  storeUrl?: string;
  releaseNotes?: string;
  updateTitle?: string;
  updateMessage?: string;
  releaseDate?: string;
  isMaintenanceMode: boolean;
  maintenanceMessage?: string;
  featureFlags?: Record<string, boolean>;
  isActive: boolean;
  updatedAt: string;
}

export interface AllVersionsResponse {
  android?: VersionConfig;
  ios?: VersionConfig;
  web?: VersionConfig;
}

// Get current web version from environment
const APP_VERSION = process.env.REACT_APP_VERSION || '1.0.0';

// Local storage keys
const VERSION_CHECK_KEY = 'version_check_result';
const VERSION_CHECK_TIMESTAMP = 'version_check_timestamp';

// Listeners for version info changes
type VersionListener = (versionInfo: VersionCheckResponse | null) => void;

class VersionControlService {
  private cachedResult: VersionCheckResponse | null = null;
  private lastCheckTime: number = 0;
  private listeners: Set<VersionListener> = new Set();
  private lastAskedAt: number | null = null;

  /** At most one ask per this long, however often pages change. */
  static readonly RECHECK_MS = 5 * 60 * 1000;

  /**
   * Ask the backend whether this bundle is out of date (throttled).
   *
   * The bell route rather than POST /version-control/check because it takes
   * the organisation from the JWT; /check trusts an organizationId in the query
   * string. A failure is silent: no answer means no popup, never an error toast
   * on page load.
   */
  async checkNow(): Promise<void> {
    const now = Date.now();
    if (this.lastAskedAt !== null && now - this.lastAskedAt < VersionControlService.RECHECK_MS) return;
    this.lastAskedAt = now;
    try {
      const body: any = await apiClient.get('/bell-notifications/recent', {
        params: { limit: 1, channel: 'web', platform: 'web', clientVersion: APP_VERSION },
      });
      const info = body?.data?.versionInfo ?? body?.versionInfo ?? null;
      if (info) this.setVersionInfo(info);
    } catch {
      /* no answer, no popup */
    }
  }

  /**
   * Subscribe to version info changes
   */
  subscribe(listener: VersionListener): () => void {
    this.listeners.add(listener);
    // Immediately notify with current value
    if (this.cachedResult) {
      listener(this.cachedResult);
    }
    // Return unsubscribe function
    return () => this.listeners.delete(listener);
  }

  /**
   * Set version info from external source (notification API)
   * This avoids making a separate API call for version check
   */
  setVersionInfo(versionInfo: VersionCheckResponse | null): void {
    if (versionInfo) {
      this.cachedResult = versionInfo;
      this.lastCheckTime = Date.now();
      localStorage.setItem(VERSION_CHECK_KEY, JSON.stringify(versionInfo));
      localStorage.setItem(VERSION_CHECK_TIMESTAMP, this.lastCheckTime.toString());
    }
    // Notify all listeners
    this.listeners.forEach(listener => listener(versionInfo));
  }

  /**
   * Get current cached version info (for initial render)
   */
  getCachedVersionInfo(): VersionCheckResponse | null {
    if (this.cachedResult) {
      return this.cachedResult;
    }
    // Try to load from localStorage
    // A stored answer is only good for the bundle that asked for it. After a
    // deploy, a plain F5 loads the new bundle while localStorage still holds
    // the old bundle's "update available" — replaying it would show a popup
    // for the version the user is already on, until checkNow() answered.
    const cachedData = localStorage.getItem(VERSION_CHECK_KEY);
    if (cachedData) {
      try {
        const parsed: VersionCheckResponse = JSON.parse(cachedData);
        return parsed?.clientVersion === APP_VERSION ? parsed : null;
      } catch {
        return null;
      }
    }
    return null;
  }

  /**
   * Get current app version
   */
  getCurrentVersion(): string {
    return APP_VERSION;
  }

  /**
   * Get all version configs (admin only)
   */
  async getAllVersions(): Promise<AllVersionsResponse> {
    return await apiClient.get<AllVersionsResponse>('/version-control') as AllVersionsResponse;
  }

  /**
   * Save version config (admin only)
   */
  async saveVersionConfig(config: Partial<VersionConfig>): Promise<VersionConfig> {
    return await apiClient.post<VersionConfig>('/version-control', config) as VersionConfig;
  }

  /**
   * Update version config (admin only)
   */
  async updateVersionConfig(id: string, config: Partial<VersionConfig>): Promise<VersionConfig> {
    return await apiClient.put<VersionConfig>(`/version-control/${id}`, config) as VersionConfig;
  }

  /**
   * Toggle maintenance mode (admin only)
   */
  async toggleMaintenanceMode(
    platform: 'web' | 'android' | 'ios',
    isMaintenanceMode: boolean,
    maintenanceMessage?: string
  ): Promise<VersionConfig> {
    return await apiClient.post<VersionConfig>(
      `/version-control/${platform}/maintenance`,
      { isMaintenanceMode, maintenanceMessage }
    ) as VersionConfig;
  }

  /**
   * Initialize default versions (admin only)
   */
  async initializeDefaultVersions(): Promise<void> {
    await apiClient.post('/version-control/initialize');
  }

  /**
   * Perform hard reload to get new version
   */
  performUpdate(): void {
    // Clear all caches
    localStorage.removeItem(VERSION_CHECK_KEY);
    localStorage.removeItem(VERSION_CHECK_TIMESTAMP);

    // Clear service worker cache if exists
    if ('caches' in window) {
      caches.keys().then(names => {
        names.forEach(name => {
          caches.delete(name);
        });
      });
    }

    // Hard reload the page
    window.location.reload();
  }

  /**
   * Clear version check cache
   */
  clearCache(): void {
    this.cachedResult = null;
    this.lastCheckTime = 0;
    localStorage.removeItem(VERSION_CHECK_KEY);
    localStorage.removeItem(VERSION_CHECK_TIMESTAMP);
  }
}

export const versionControlService = new VersionControlService();
export default versionControlService;
